import { useState } from 'react'
import { useOutletContext } from 'react-router'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Container } from '@/components/ui/container'
import { DownloadCloud02Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { GalleryShowcaseModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-showcase-modal/gallery-showcase-modal'
import { PhotosetStatusMessage } from '@/features/photoset/components/photoset-status-message/photoset-status-message'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useGetPhotosetImagesList } from '@/features/photoset/queries/use-get-photoset-images-list'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { useTranslation } from '@/translations/i18n'

export const PhotosetPublicShowcase = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [currentDetailImageIndex, setCurrentDetailImageIndex] = useState<
		number | null
	>(null)
	//queries
	const { data: photoset, isFetching: isFetchingPhotoset } = useGetPhotoset({
		id: urlParams.id
	})
	const { data: images, isFetching: isFetchingImages } =
		useGetPhotosetImagesList({
			urlParams: { photosetId: urlParams.id }
		})
	const isFetching = isFetchingPhotoset || isFetchingImages

	if (!(isFetching || photoset?.thumbnail_generated_at)) {
		return (
			<Container className='flex flex-col items-center justify-center'>
				<PhotosetStatusMessage gap={3}>
					<Spinner className='fill-neutral-black' size={32} />
					<h1 className='font-bold text-display-xs text-neutral-900'>
						{t('photoset:show.thumb_gen_title')}
					</h1>
					<p className='text-neutral-900 text-xl'>
						{t('photoset:show.thumb_gen_subtitle')}
					</p>
				</PhotosetStatusMessage>
			</Container>
		)
	}

	return (
		<div className='grid grid-rows-[auto_1fr] overflow-auto'>
			<PageHero
				endSlot={
					photoset?.zip_url && (
						<ButtonLinkLegacy
							disabled={isFetching}
							size='sm'
							to={photoset.zip_url}
							variant='secondary'
						>
							{t('photoset:imagen_ai.completed.download_all_set')}
							<DownloadCloud02Icon />
						</ButtonLinkLegacy>
					)
				}
				subtitle={t('photoset:show.paragraph')}
				title={t('photoset:show.title')}
			/>

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					<div className='grid w-full grid-cols-4'>
						{isFetching &&
							[...new Array(20).keys()].map((e) => (
								<ImageCard asSkeleton key={`${e}-id`} />
							))}
						{!isFetching &&
							images?.map((image, index) => (
								<ImageCard
									endSlot={
										image.thumbnails?.xs && (
											<ExpandImageButton
												onClick={() =>
													setCurrentDetailImageIndex(
														index
													)
												}
											/>
										)
									}
									hideCheckbox
									image={image}
									isSelected={false}
									key={image.id}
									onSelect={() =>
										setCurrentDetailImageIndex(index)
									}
									thumbnail={image.thumbnails?.xs}
								/>
							))}
					</div>
				</PhotosetStepBodyContainer>

				{currentDetailImageIndex !== null && images && (
					<GalleryShowcaseModal
						currentDetailImageIndex={currentDetailImageIndex}
						onClose={() => setCurrentDetailImageIndex(null)}
						selectableImages={images}
						setCurrentDetailImageIndex={setCurrentDetailImageIndex}
					/>
				)}
			</PhotosetStepWrapper>
		</div>
	)
}

PhotosetPublicShowcase.displayName = 'PhotosetCurationStep'
